<template>
    <div class="flex flex-col gap-4">
        <!-- Question -->
        <div class="richtext richtext--lg richtext--tight js-richtext-video" v-html="question.question"></div>

        <!-- Question Audio Player -->
        <div class="grid gap-3" v-if="question.questionAudio != null">
            <QuizAudioPlayer
                :url="question.questionAudio.url"
                :transcript="question.questionAudioTranscript"
            />
        </div>

        <ul class="flex flex-col gap-3">
            <!-- Answers -->
            <li class="flex flex-col gap-3" v-for="(a, i) in displayAnswers" :key="a.id">
                <!-- Answered Correctly -->
                <div class="flex flex-col gap-1.5">
                    <div :class="[answerClassnames, getAnswerBackground(a)]">
                        <!-- Prefix -->
                        <span class="">
                            <strong>{{ letters[i] }}.</strong>
                        </span>
                        <div class="text-left" v-html="a.content"></div>
                    </div>
                    <p class="flex justify-start items-center gap-3 text-xs" v-if="a && getAnswerText(a)">
                        <span class="flex justify-center items-center w-3.5 h-3.5 rounded-full" :class="getIconBackground(a)">
                            <Icon
                                :name="getIconName(a)"
                                dir="/icons/icon-selector/"
                                class="w-2 h-2"
                            />
                        </span>
                        <span>
                            {{ getAnswerText(a) }}
                            <span v-if="shouldShowYourAnswer(a)">(Your answer)</span>
                        </span>
                    </p>
                </div>

                <!-- If this answer os correct, show the reveal copy -->
                <div class="pl-7 pb-2 " v-if="a.isCorrectAnswer && question.reveal?.copy && graded !== true">
                    <div class="richtext richtext--lg" v-html="question.reveal.copy"></div>
                    <QuizAudioPlayer
                        class="mt-2"
                        v-if="question.reveal.audio"
                        :url="question.reveal.audio.url"
                        :transcript="question.reveal.audioLabel"
                    />
                </div>
            </li>
        </ul>
    </div>
</template>

<script setup>
    import { ref, onMounted } from 'vue';
    import Icon from '../pieces/Icon.vue';
    import QuizAudioPlayer from './QuizAudioPlayer.vue';
    const props = defineProps({
        question: null,
        attempt: null,
        graded: true
    });

    const letters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

    const answerClassnames = 'inline-flex justify-start items-start gap-2 border rounded-md px-4 py-2 w-full';
    const answers = ref([]);
    const displayAnswers = ref([]);

    const trimAnswers = () => {
        displayAnswers.value = answers.value;
    };

    const shouldShowAnswer = (answer) => {
        return true;
    };

    const getAnswerBackground = (answer) => {
        if (answer.selected && answer.isCorrectAnswer) return 'bg-green-1';
        if (!answer.selected && answer.isCorrectAnswer) return 'bg-green-1';
        if (answer.selected && !answer.isCorrectAnswer) return 'bg-red-2';
        return 'bg-gray-11';
    };

    const getIconBackground = (answer) => {
        return props.question.answeredCorrectly || answer.isCorrectAnswer ? 'bg-green-3' : 'bg-red-default';
    };

    const getIconName = (answer) => {
        if (answer.selected && answer.isCorrectAnswer) return 'check';
        if (!answer.selected && answer.isCorrectAnswer) return 'check';
        if (answer.selected && !answer.isCorrectAnswer) return 'close';
        return 'neutral';
    };

    const getAnswerText = (answer) => {
        if (answer.selected && answer.isCorrectAnswer) return 'Correct!';
        if (!answer.selected && answer.isCorrectAnswer) return 'Correct Answer';
        if (answer.selected && !answer.isCorrectAnswer) return 'Incorrect Answer';
        return '';
    };

    const shouldShowYourAnswer = (answer) => {
        if (!props.graded) {
            // Find the attempt for this specific question
            const questionAttempt = props.attempt?.find(a => a.questionId === props.question?.questionId);
            
            // Check if this answer was selected in the attempt
            const isSelected = questionAttempt?.answers?.some(a => a.answerId === answer.answerId);
            return isSelected;
        }

        // In graded mode, only show the answer that was actually selected
        return answer.selected;
    };

    onMounted(() => {
        answers.value = props.question['answers'];

        if (!props.graded && props.attempt) {
            const questionAttempt = props.attempt.find(a => a.questionId === props.question.questionId);
            if (questionAttempt) {
                answers.value = answers.value.map(answer => {
                    const isSelected = questionAttempt.answers.some(a => a.answerId === answer.answerId);
                    return {
                        ...answer,
                        selected: isSelected,
                        isCorrectAnswer: answer.isCorrectAnswer
                    };
                });
            }
        }
        
        trimAnswers();
    });
</script>
